import dayjs from "dayjs";
import { ExternalLink } from "lucide-react";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
	FloatingPanel,
	FloatingPanelBody,
	FloatingPanelContent,
	FloatingPanelDescription,
	FloatingPanelFooter,
	FloatingPanelHeader,
	FloatingPanelTitle,
	FloatingPanelTrigger,
} from "@/components/Core/FloatingPanel";
import { AbsenceType } from "@/configs/constants/AbsencesType";
import { TIME_REPORT_STATUS } from "@/configs/constants/TimeReportStatus";
import TimeReport from "@/models/TimeReport";
import { formatNumber } from "@/utils/date.time.utils";

type StatusLabelKey =
	| "INVOICED"
	| "ATTESTED_INTERNALLY"
	| "APPROVED"
	| "SILENT_ATTESTED"
	| "DECLINED"
	| "NOT_WORKED"
	| "REPORTED"
	| "PENDING_REPORT";

/** Same status → label/color mapping as the TimeReportsPage table. */
function statusPresentation(report: TimeReport): { labelKey: StatusLabelKey; className: string } {
	if (report.invoiceBatch && report.status === TIME_REPORT_STATUS.SILENT_ATTESTED) {
		return { labelKey: "INVOICED", className: "text-gray-500" };
	}
	switch (report.status) {
		case TIME_REPORT_STATUS.ATTESTED_BY_ADMIN:
			return { labelKey: "ATTESTED_INTERNALLY", className: "text-gray-500" };
		case TIME_REPORT_STATUS.ATTESTED_BY_CUSTOMER:
		case TIME_REPORT_STATUS.RESOLVED_DECLINE_BY_ADMIN:
			return { labelKey: "APPROVED", className: "text-success" };
		case TIME_REPORT_STATUS.SILENT_ATTESTED:
			return { labelKey: "SILENT_ATTESTED", className: "text-success" };
		case TIME_REPORT_STATUS.DECLINED_BY_CUSTOMER:
			return { labelKey: "DECLINED", className: "text-danger" };
		case TIME_REPORT_STATUS.INVOICED:
			return { labelKey: "INVOICED", className: "text-gray-500" };
		case TIME_REPORT_STATUS.NOT_WORKED:
			return { labelKey: "NOT_WORKED", className: "text-danger" };
		case TIME_REPORT_STATUS.WORKED:
			return { labelKey: "REPORTED", className: "text-gray-500" };
		case TIME_REPORT_STATUS.PENDING:
		default:
			return { labelKey: "PENDING_REPORT", className: "text-gray-500" };
	}
}

export interface JobTimeReportsPanelProps {
	timeReports: TimeReport[];
	/** The full time reports page pre-filtered on the job (opens in a new tab). */
	timeReportsHref: string;
	/** The toolbar button that opens the panel. */
	trigger: React.ReactElement;
}

/**
 * "Tidrapporter" companion panel for the Matchning tab: a non-modal
 * FloatingPanel listing the job's time reports (day, consultant, reported or
 * planned times, status) so they can be checked without leaving the planner.
 * The footer links to the real time reports page for anything beyond reading.
 */
const JobTimeReportsPanel: React.FC<JobTimeReportsPanelProps> = ({
	timeReports,
	timeReportsHref,
	trigger,
}) => {
	const { t } = useTranslation();

	const rows = useMemo(
		() =>
			[...timeReports].sort(
				(a, b) => dayjs(a.shift.start).valueOf() - dayjs(b.shift.start).valueOf()
			),
		[timeReports]
	);

	return (
		<FloatingPanel>
			<FloatingPanelTrigger asChild>{trigger}</FloatingPanelTrigger>
			{/* Same treatment as the consultant picker: a near-transparent
			    backdrop blocks the page and closes the panel on click */}
			<FloatingPanelContent
				className="w-[30rem]"
				aria-describedby={undefined}
				overlay
				overlayClassName="bg-black/10"
			>
				<FloatingPanelHeader>
					<div>
						<FloatingPanelTitle>{t("shiftPlanner.timeReports")}</FloatingPanelTitle>
						<FloatingPanelDescription>{t("shiftPlanner.timeReportsHint")}</FloatingPanelDescription>
					</div>
				</FloatingPanelHeader>

				<FloatingPanelBody className="px-0 py-0">
					{rows.length === 0 ? (
						<p className="px-5 py-6 text-sm text-gray-500">{t("shiftPlanner.noTimeReports")}</p>
					) : (
						rows.map((report) => {
							const status = statusPresentation(report);
							const times = report.reported ?? report.shift;
							return (
								<div
									key={report.id}
									className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-2.5"
								>
									<div className="min-w-0">
										<p className="text-sm font-medium capitalize">
											{dayjs(report.shift.start).locale("sv").format("ddd D MMM")}
										</p>
										<p className="truncate text-xs text-gray-500">
											{report.consultant
												? `${report.consultant.firstName} ${report.consultant.lastName}`
												: "–"}
										</p>
									</div>
									<div className="shrink-0 text-right">
										<p className="text-sm tabular-nums">
											{dayjs(times.start).format("HH:mm")} - {dayjs(times.end).format("HH:mm")}
											{!!times.break &&
												` · ${t("shiftPlanner.breakShort", {
													hours: formatNumber(times.break, 1),
												})}`}
										</p>
										<p className={`text-xs ${status.className}`}>
											{report.absence
												? t(report.absence.type as `${AbsenceType}`)
												: t(status.labelKey)}
										</p>
									</div>
								</div>
							);
						})
					)}
				</FloatingPanelBody>

				<FloatingPanelFooter className="sm:justify-start">
					<a
						href={timeReportsHref}
						target="_blank"
						rel="noreferrer"
						className="flex items-center gap-1.5 text-sm text-action underline hover:no-underline"
					>
						<ExternalLink className="h-3.5 w-3.5" />
						{t("shiftPlanner.openTimeReports")}
					</a>
				</FloatingPanelFooter>
			</FloatingPanelContent>
		</FloatingPanel>
	);
};

export default JobTimeReportsPanel;
